import { TOnSlideChangeParams } from '@cian/frontend-fullscreen-carousel-component';
import { NewbuildingCarousel as CoworkingCarousel } from '@cian/frontend-newbuilding-carousel-component';
import { Image } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { Component } from 'react';

import { IPhotoSchema } from '../../repositories/coworking/entities/photo/PhotoSchema';
import { Counter } from '../UI/Counter';
import { FullscreenGallery } from '../UI/FullscreenGallery';
import { Layout } from '../UI/Layout';

import { ArrowControls, TDirection } from './parts/ArrowControls';
import { NoImage } from './parts/NoImage';
import { firstPaintOptions, mediaSettings } from './settings';
import { ICarouselProps, ICarouselState } from './types';

import * as styles from './Carousel.css';

export class Carousel extends Component<ICarouselProps, ICarouselState> {
  public constructor(props: ICarouselProps) {
    super(props);

    const { media } = this.props;

    this.state = {
      slides: this.prepareSlides(media.slice(0, 10)),
      clickedSlideIndex: null,
      currentSlide: 2,
    };
  }

  public componentDidMount() {
    const { media } = this.props;
    this.setState({ slides: this.prepareSlides(media) });
  }

  /* istanbul ignore next */
  private handleClickSlide = (slideIndex: number) => {
    this.setState({ clickedSlideIndex: slideIndex + 1 });
  };

  /* istanbul ignore next */
  private changeCurrentSlide = (params: TOnSlideChangeParams) => {
    this.setState({ currentSlide: params.currentSlide });
  };

  /* istanbul ignore next */
  private handleCloseFullscreenGallery = () => {
    this.setState({ clickedSlideIndex: null });
  };

  private onChangeFullscreenGallerySlide = (params: TOnSlideChangeParams) => {
    const { currentSlide } = params;

    /* istanbul ignore next */
    this.setState({ currentSlide, clickedSlideIndex: currentSlide });
  };

  /* istanbul ignore next */
  private handleArrowControlsClick = (direction: TDirection) => {
    this.setState({ currentSlide: this.state.currentSlide + direction * 1 });
  };

  public prepareSlides = (mediaData: IPhotoSchema[]): React.ReactNode[] => {
    return mediaData.map(media => (
      <Image alt={media.cianFull} key={media.cianFull} objectFit="cover" src={media.cianFull} />
    ));
  };

  public render() {
    const { slides, currentSlide, clickedSlideIndex } = this.state;
    const { title, media } = this.props;

    if (!slides || !slides.length) {
      return (
        <div className={styles['carousel']}>
          <NoImage />
        </div>
      );
    }

    if (slides.length === 1) {
      return <div className={clsx(styles['carousel'], styles['single-photo'])}>{slides[0]}</div>;
    }

    const fullscreenGallerySlideIndex = clickedSlideIndex;
    const isFullscreenGalleryVisible = fullscreenGallerySlideIndex !== null;

    const defaultFullscreenGalleryIndex = fullscreenGallerySlideIndex || 0;

    return (
      <>
        <div className={styles['carousel']} data-nav-tab="carousel/Фотографии" id="carousel">
          <CoworkingCarousel
            defaultCurrentSlide={currentSlide}
            firstPaintOptions={firstPaintOptions}
            media={mediaSettings}
            slides={slides}
            onSlideChange={this.changeCurrentSlide}
            onSlideClick={this.handleClickSlide}
          />
          <ArrowControls
            isLeftButtonVisible={currentSlide > 2}
            isRightButtonVisible={currentSlide < slides.length}
            onButtonClick={this.handleArrowControlsClick}
          />
          <Layout filled={false}>
            <Counter className={styles['counter']} currentSlide={currentSlide} totalSlides={slides.length} />
          </Layout>
        </div>

        {isFullscreenGalleryVisible && (
          <FullscreenGallery
            defaultSlideIndex={defaultFullscreenGalleryIndex}
            images={media}
            title={title}
            onClose={this.handleCloseFullscreenGallery}
            onSlideChange={this.onChangeFullscreenGallerySlide}
          />
        )}
      </>
    );
  }
}
